import ApexCharts from "apexcharts";
import { RefObject, useCallback, useEffect, useRef } from "react";

import { CellOutcome } from "@components/ChartCapture/types";
import { captureFromChart } from "@components/ChartCapture/utils/captureFromChart";

// A chart that hangs (no data / stuck query) must not wedge the run forever.
const READINESS_TIMEOUT_MS = 15000;

/**
 * Owns the per-cell "capture exactly once" lifecycle: signal `onStart` on mount
 * (the chart is now rendering), then fire `onResult` once the chart signals
 * readiness (after a paint so the canvas exists), or once the timeout trips.
 */
export const useCaptureCell = (
  chartRef: RefObject<ApexCharts | null>,
  onResult: (outcome: CellOutcome) => void,
  onStart?: () => void,
) => {
  const doneRef = useRef(false);
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    onStartRef.current?.();
  }, []);

  const finish = useCallback(
    (outcome: CellOutcome) => {
      if (doneRef.current) return;
      doneRef.current = true;
      onResult(outcome);
    },
    [onResult],
  );

  const handleReady = useCallback(
    async (ready: boolean) => {
      if (!ready || doneRef.current) return;
      return finish(await captureFromChart(chartRef.current));
    },
    [chartRef, finish],
  );

  useEffect(() => {
    const timer = setTimeout(
      () => finish({ imageUri: null, hasData: false, captured: false, timedOut: true }),
      READINESS_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [finish]);

  return handleReady;
};
